import { DEFAULT_TIME_ZONE_CONFIG } from './timeZoneConfig';

/** @type {typeof DEFAULT_TIME_ZONE_CONFIG} */
let formatSettings = { ...DEFAULT_TIME_ZONE_CONFIG };

/**
 * 由會員前台啟動時注入 tenant_config.timeZone 設定。
 * 後台管理維持預設 yyyy-MM-dd 24 小時制。
 */
export function setFormatDateTimeSettings(next) {
  formatSettings = {
    ...DEFAULT_TIME_ZONE_CONFIG,
    ...(next && typeof next === 'object' ? next : {}),
  };
}

export function getFormatDateTimeSettings() {
  return { ...formatSettings };
}

/**
 * 將 API 回傳的 ISO 時間轉為使用者友善格式（依 timeZone 設定）。
 * @param {string|number|Date|null|undefined} value
 * @param {string} [fallback='—']
 * @returns {string}
 */
export function formatDateTime(value, fallback = '—') {
  if (value == null || value === '') {
    return fallback;
  }

  const date = parseDateTimeValue(value, formatSettings);
  if (!date) {
    return fallback;
  }

  return formatDateTimeParts(date, formatSettings);
}

function hasExplicitOffset(text) {
  return /[zZ]$|[+-]\d{2}:\d{2}$/.test(text.trim());
}

function siteOffsetToIana(offset) {
  if (!offset || offset === 'Z') {
    return 'UTC';
  }
  const match = String(offset).match(/^([+-])(\d{2}):?(\d{2})?$/);
  if (!match) {
    return 'UTC';
  }
  const hours = parseInt(match[2], 10);
  if (hours === 0) {
    return 'UTC';
  }
  const etcSign = match[1] === '+' ? '-' : '+';
  return `Etc/GMT${etcSign}${hours}`;
}

function parseDateTimeValue(value, settings) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const text = String(value).trim();
  if (!text) {
    return null;
  }

  const isoLike = text.includes('T') ? text : text.replace(' ', 'T');

  if (hasExplicitOffset(isoLike)) {
    const date = new Date(isoLike);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const withOffset = settings.timeZone === 'Z'
    ? `${isoLike}Z`
    : `${isoLike}${settings.timeZone}`;
  const date = new Date(withOffset);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTimeParts(date, settings) {
  const timeZone = settings.useBrowserTimezone
    ? undefined
    : siteOffsetToIana(settings.timeZone);

  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: settings.timeFormat === '12h',
    hourCycle: settings.timeFormat === '12h' ? 'h12' : 'h23',
  });

  const parts = formatter.formatToParts(date);
  const pick = (type) => parts.find((part) => part.type === type)?.value || '00';

  const year = pick('year');
  const month = pick('month');
  const day = pick('day');
  const minute = pick('minute');
  const second = pick('second');
  const dayPeriod = pick('dayPeriod');

  let hour = pick('hour');
  if (settings.timeFormat === '12h' && dayPeriod) {
    hour = `${hour} ${dayPeriod}`;
  }

  const dateStr = settings.dateFormat === 'dd-MM-yyyy'
    ? `${day}-${month}-${year}`
    : `${year}-${month}-${day}`;

  return `${dateStr} ${hour}:${minute}:${second}`;
}
